import {useEffect, useState} from 'react';
import AvatarWithProgress from '@jobseeker/lotus/components/AvatarWithProgress';
import {Link, List, Typography, Box, SxProps} from '@mui/material';
import {
  getOldLink,
  getRefFromPath,
  truncateString,
  forwardQueryParamIfMatches,
} from '@jobseeker/lotus/utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {Button, Pro} from '@jobseeker/lotus/elements';
import ListItem from '@mui/material/ListItem';
import {useSelector} from 'react-redux';
import {SwitchOff} from '@jobseeker/lotus/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import moment from 'moment';
import {FaChevronDown} from 'react-icons/fa';
import type {ProfileDrawerOption} from '../ProfileDrawer/types';
import type {ProfileDropProps} from './types';
import SpotLightBadge from '@jobseeker/lotus/components/SpotLightBadge';
import type {AppState} from '@jobseeker/common-core/types';
import {deleteCookie} from 'cookies-next';

const LinkItem = ({
  option,
  index,
  sx = {},
}: {
  option: ProfileDrawerOption;
  index: number;
  sx?: SxProps;
}) => (
  <ListItem
    component="a"
    key={`profiledrawer-list-item-${index}`}
    title={option?.label}
    href={getOldLink(option['url'])}
    target="_blank"
    sx={{
      paddingY: 1,
      fontSize: '16px',
      cursor: 'pointer',
      color: '#555',
      ...sx,
    }}
  >
    {option['label']}
  </ListItem>
);

const ProfileDrop = ({
  theme,
  name,
  email,
  profileProgress,
  profilePicUrl,
  proMembership,
  OnLogoutBtnClick,
  profileDrawerOptions,
  followUps,
  isOnline,
  showPremium,
  chatCount,
  expandProfile,
  subscriptions,
  showSpotLight,
  page,
}: ProfileDropProps) => {
  const {profileStatus} = useSelector(
    (state: AppState) => state.user.completeness
  );
  const [premiumPurchase, setPremiumPurchase] = useState<
    {
      title: string;
      actionLabel: string;
      actionUrl: string;
    }[]
  >();
  const listData2 = profileDrawerOptions.slice(3, profileDrawerOptions.length);

  useEffect(() => {
    const newPremiumPurchase = [];

    if (subscriptions?.earlyApplicant?.isValid) {
      newPremiumPurchase.push({
        title: 'Early Applicant',
        actionLabel: `Valid till ${moment(
          subscriptions?.earlyApplicant?.validUpTo,
          'DD-MM-YYYY'
        ).format('DD MMM YYYY')}`,
        actionUrl: getOldLink(`/subscribe/early-applicant-preference`),
      });
    }

    if (subscriptions?.resumeBuilder?.isValid) {
      newPremiumPurchase.push({
        title: 'Resume Builder',
        actionLabel: `Valid till ${moment(
          subscriptions?.resumeBuilder?.validUpTo,
          'DD-MM-YYYY'
        ).format('DD MMM YYYY')}`,
        actionUrl: getOldLink(`/subscribe/resume-builder`),
      });
    }
    setPremiumPurchase(newPremiumPurchase);
  }, []);

  return (
    <Box
      sx={{
        width: {md: '370px'},
        position: 'relative',
        float: {md: 'right'},
        height: {md: '63vh'},
        overflowY: 'auto',
        borderRadius: {md: '5px'},
        boxShadow: {md: '0 3px 31px 0 rgba(0, 0, 0, 0.09)'},
        border: {md: 'solid 1px #e9e9e9'},
        backgroundColor: ' #ffffff',
        '@media (min-width: 1720px)': {
          marginRight: '138px',
        },
      }}
      marginTop={-2}
    >
      <Box
        sx={{
          px: {md: 1.7},
        }}
      >
        {!expandProfile && (
          <Box
            sx={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}
          >
            <Box>
              <AvatarWithProgress
                profilePicture={profilePicUrl}
                percentage={profileProgress ?? 0}
                isOnline={isOnline}
              />
            </Box>
            <Box sx={{marginLeft: 1.55, marginTop: 2.8, width: '224px'}}>
              <Typography
                fontWeight={600}
                variant="h6"
                color={theme?.palette.secondary.main}
              >
                {truncateString(name, 26)}
                {proMembership?.isValid && (
                  <Box display="block" ml={theme?.spacing(0.5)}>
                    <Pro bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)" />
                  </Box>
                )}
              </Typography>

              <Typography
                fontSize={theme?.typography.pxToRem(14)}
                color={theme?.palette.secondary.main}
              >
                {email}
              </Typography>
              <Typography
                fontSize={theme?.typography.pxToRem(12)}
                sx={{
                  borderRadius: '3px',
                  border: 'solid 1px #fbf0dd',
                  backgroundColor: '#fffcf7',
                  padding: 0.6,
                  marginTop: 0.6,
                }}
              >
                {followUps} Follow-ups Remaining
              </Typography>

              {!!showSpotLight && <SpotLightBadge />}
            </Box>
            {email && (
              <Box>
                <Typography
                  onClick={event => {
                    event?.preventDefault();
                    localStorage.removeItem('filter');
                    deleteCookie('filter');
                    openUrl(
                      forwardQueryParamIfMatches('/registration/addPersonalDetails', 'ref', ['admin_login']),
                      '_self'
                    );
                  }}
                  style={{
                    cursor: 'pointer',
                  }}
                  sx={{
                    fontWeight: '500',
                    fontSize: '12px',
                    color: '#0057ff',
                  }}
                >
                  EDIT
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {email && profileDrawerOptions?.length > 0 && (
          <Box my={1}>
            <Box>
              {!expandProfile && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #fceecf',
                    borderRadius: '5px',
                    marginTop: 2.2,
                    padding: 0.5,
                  }}
                >
                  <Image
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaCAYAAAA4qEECAAAAAXNSR0IArs4c6QAACsxJREFUeAHtnQtsHEcZx2fWb+fuHNu5M5DSohK1goLakPAQjao+UBJBqlIgLUKtEvDFzUNpK0JLVBXlykNp3KQBopI69vESEhJRpUgpgtIiKGoEoklbQCABAQkRQu98Seyz6/PZtzv8v72bze757uxZ++LDNyudZ/Z7zM789tuZ2T3fLGN60wQ0AU1AmQBX9lB0SPWGPmRx8WXBxDouWFgwVvVjzqWKqIQQnA1zxl8xBD+wIp7+/Vz8/NpUrdEiFjOGzx36GpoDyKzBbwWvhB8gmIzzA+Gr9nyFx2JWNY5ZNdCJaPARQO53Ks1ZBhH9NzQo58gWMyNEIyL6OiZYm1MNzh/tGRp7ytlfwExVQA/vCF5vTYs/oBEtnLMcZ8YjK9ZvOMLvOW4uYN3nXZT4yeaG1AsvPCi41S8Ea0SnljWa+I3ho2N/nXfhRQU0Fu0vzO406yXIVJhgxv7IUPqbbOi4UtnJ3sBmRP9DcGotdhSMvxppizzMj5zNJqPBvdB/Cp8pXEHfisTH53ygwok/nOwNdTFmPW7XmerO2KPFx5zvflVAW0yslhVraeLfl3mllLPDQoiVpX3EmuFM8mRyZ/g1kc3slza4et6F/JxBS7/mZv697BR7nPbddZf6hUiNSoWktgXvTkYD/0bUfLeSXbEOI3mnlC0/OvpPmVdJ0Z8fg/1kKR+Uf7q5if828p3hN9H3HQdgGsAmCz6lXCrK3HV0172iU0FJbIgRsapkX7aPHu7r+IBlma/gcmqjfjZshDr4sfMTlQqTOhz8NKJxDe33xMfLHkPa10Ka6A1gcoS5J+dnIkNja+dSJ9H3jvZhKz1a6N8zhtGwLnxs9LVSviW7jrHtPZHM9Fsn4GCPyOhnh4ohp7Z3r+w+mjqPitkVLFU4yTD7GCinqyk5aKluxCQRDQ2hw9lOASlM8wTYrQ0+m0gWl1USdCY30Y/DvtM25vx3NPAwlnZ8MXg8Y05ndw5HQz+FcJOjKJURoq+U+P9NluwNbsGs5LOIqyfDg+Mvy/oTm2QmcRMG4o8QM2IH3Vapl+mMPvrCA4H3Yq5wPxkgWi/x5ta7aXSXDslo6EHBrJ20L7h4v5Qv9RRz7oPoDjcKi5+8sK3rBtleYmMzAqu8TNyfigbeI/UynRHRpsmfQIHyBPTTgCONE30d72aWeVDuG8ygqVXFzeANc+rvKhZyBZSWME9XOgwGmhOI2CgeJQRNMX1C7F71PhmAxAjjEkXyfvRAhsX5V5Hf7C7PA/rC7q6QmZm6yzbgLBE2gt9mbMyx56b5dRysyRYY/HB4MP1jR1kmEx4aPVNGVVNiDIYV6wMWDw2b6TVo/2oE4qrUZGIXHJ6WTsQqaaUfRl/dg2v9LmLZfeSi09/KyLXtAXk9zogNEjON4+4BEHPWt8HoXtuQ85TR2bJPHqQeUpuF0fgFRDZYY/iz2GMidkOzbDvpiRntE0NiKXWUekCjV/64VOIKeF7mKeXZyU04gj1Vw59j4f7U5VB3Gy7hfGRw5A0MXM/lmyi6k+fP3epprmXQ5KCwXWZJgiLQ7HppFgmEfiPzlOJR521yv4HxkzJfdyk3nDtPbpmfcLc/HAq97Np3WJLMAxoR+3bbkPOL/PC5jMsJocyd2+Gu5W1/cuvqKd8uWlww+XXuthMzmqmRzGFZMPCAhpr6YbJyZhoFO0rQyWPjbIIfTLxl5+vwz7LBRJLulO2mC5EPTC+HArsCy4LOA5oLbs+XUVCL19c+Q/YIivvANtG3Jj/zKDaqg326E8ZgZ385gLm1h1+h+fYAKVlKJF5Dno9kFJSPbGmVT/9LCQ2ISXb2aq+qfvbSu4LdaG0BJrOZuFvvsCuwlDovaCZk2C8b2x0ISyNKDSGchyXcEhvdunrKZyfZR2V7BTc8TybpGRFCcZmtF8JzEjygMeC9KgvJZowNMk+paGq8PNMQYqtbV095dBeflu1tYOLnMk9pNpdZL/fRxXjuND2gGRfOPNBi1ibpRGnk2ZHXMRDasw3chq61vwFxG9RBHg/TViHkPkdNBcjxbiP4orvZHmYulmTjAb1i5dpTiOoRUqAv/uSFnV35J3gkwGZw/lg+h7+cD+QP7EiWdEbEbsWXudYA3fVRQwXnT7vvnC/2dV5NzEhHDIkl5eXmAc1jv6Zpy4CtxHd+ZnY6Jg0pDQ+OPY8zaXchGHo7cQXgWWx9bKn/nPkGou92ai3ujN80OpsPulueM3P7oG8pyAYKLB0TD2iSNjcbBwBzNG8htiT7gusca2Qa2pruw6H+QjKc3WvcuqWcx7MN+tKWtil8OfwZ9yOIPCOxhZTEjhhS3r3NAI3vzy7hzNiGuBQahCWeo8tCOtETqfam9tvwiPSLTU2Nd0r5Uk+5wZ5CKP8Z0XxfT3zM6RbsLgOMiJXNAOxshkVAPI9JpS68YWN/8hc/ux3AP4ZPJGflfgTdLVJf+KrmsNyvhzQyOEbBNyNSbTZgVGDwS2LH4s7jEAfNjIgmDf2/Q6sw7sVl8A/aR3/8YfoikvJ6u0yAmBAbkhCrVmbcU+6fhEqCJseOePoihtaNKCDOuLHNPcKSXm+AS/8VQGyIEVjZzMqAKdl1SNtIPH0W+ajc1+lMAj1D6R9CSp+KW9mIruillcoENGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9NGhlZP4cNGh/3JS9Kv7Hv3JpJRyW8rp3JZpbVlR10PilUV/Zo9eRQncdV+hkVz2il8q6d/M9H1UHvZDr3tGScHx60l6wUDS1nnYvfjhfELOtezff8qsOer4VJP9Lu5ZfMzWZi2Ot6DvwU+D8ls1ggdnAS80tjdHOZ0b+JcW1mtZ8H03rfE5PmrROyB0zIOIn1ND9sdRaoDNsF1lQ06BpDX6L8R/QeqDECb9QPYtfqj5BHzsPGXQh2wa2i8yy4uFruutIvfTiLfit9QepBVi57A2s/3mz/Kk0fofdj0WyT2Epi5vIhmxh9quKrV1EZU1HNFZkd1bq5RzriLhWZKc8ySQ7t62U1VJa06Cxxl63hIXlc1IyL1O3DOtodEl5LaY1DZoZhrMEnGWJGS8r8Mi48XotApZ1quk+mtYBneCZcfTBASxDu2M4Gkgyo5XeZoFlhaceYMLaQXkMjOPeNUNJWltbTUd0YCiZwAKVXyJkmD9jXUMWs8zJ8/QB5H0kIx3+7CFbytfqVhXQmHLZK85So0d2dFw7n8aH4+MD3OB7QdNeN9VTFmSkCw+N2VHu0SnsuOvorrtCEbOaVgW0wbjTX2anxdZZazGLAa1nhEnyaoOzGLqJk/ShPMkKax3NUkJl9dSU+Ly0cNddyhYitS+9hSjIXYZ+KZmbRj5fFdBUdInX7E1guvZ3jFy5mdVYBMkVfs1e1WYdkav2HMKLI5fjwX/+xZGCtWPwupFWJayZrVAVRJt8ceQhxmJVqV7VIlrWll6FanKxF/OGmxHRNfcqVMxZTjUI/mS1X4UqeehUE9AENAEFAv8Dtr6j6p1E0F4AAAAASUVORK5CYII="
                    alt="Download"
                    width={30}
                    height={30}
                  />
                  <LinkItem
                    option={profileDrawerOptions[0]}
                    index={0}
                    sx={{color: '#e9630c'}}
                  />
                  <FaChevronDown
                    cursor="pointer"
                    color="#e9630c"
                    style={{transform: 'rotate(270deg)'}}
                    width="14px"
                    height="14px"
                    fontSize="14px"
                  />
                </Box>
              )}
              {!showPremium ? (
                <>
                  {premiumPurchase && premiumPurchase?.length > 0 && (
                    <Box
                      sx={{
                        mx: {xxs: 2, md: 0},
                        marginTop: 2.2,
                        borderRadius: '10px',
                        border: 'solid 1px #f5582d',
                        backgroundColor: '#fefbf1',
                        color: '#f5582d',
                      }}
                    >
                      <List>
                        {premiumPurchase?.map((premiumInfo, index) => {
                          return (
                            <Box
                              display="flex"
                              flexDirection="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                borderBottom:
                                  index !== premiumPurchase.length - 1
                                    ? 'solid 1px #f5582d'
                                    : 'none',
                              }}
                            >
                              <LinkItem
                                sx={{
                                  width: 'auto',
                                  fontWeight: 'bold',
                                  color: '#f5582d',
                                  marginRight: '',
                                }}
                                option={{
                                  label: premiumInfo?.title,
                                  url: premiumInfo?.actionUrl,
                                }}
                                index={index}
                                key={index}
                              />
                              <Box display="flex">
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    width: 'auto',
                                    marginRight: '7px',
                                    fontWeight: 300,
                                  }}
                                >
                                  {premiumInfo.actionLabel}
                                </Typography>
                                <FaChevronDown
                                  cursor="pointer"
                                  color="#e9630c"
                                  style={{
                                    transform: 'rotate(270deg)',
                                    marginRight: '4px',
                                  }}
                                  width="14px"
                                  height="14px"
                                  fontSize="14px"
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                </>
              ) : (
                !expandProfile && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    sx={{
                      backgroundColor: '#fffaee',
                      border: 'solid 1px #f0e2c3',
                      borderRadius: '8px',
                    }}
                    paddingX={1.5}
                    paddingTop={1.2}
                    paddingBottom={1.5}
                    marginTop={2}
                  >
                    <Box sx={{verticalAlign: 'top'}}>
                      <Image
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADQAAAAiCAMAAAAnKQpfAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAABIUExURUdwTP//4f+/Kf/FLv+/KP/CLf+/KP+/Kf/AKf/KNv/AKf+/KP+/KP/AKv+/Kf+/Kf+/KP+/KP/BK/+/Kf+/KP+/Kf+/Kf++KAX0DW0AAAAXdFJOUwABqQ+/Gfv0PARS6uBEebRp8CnXyJSBRhWSlQAAAZRJREFUOMvFVduygyAMVJSLoIKg3f//0xOo9UCFzvSpOx2nQBI3mwS77tfgzBjGv3KZ+AbC9p2XBePcwrYt+kHrMma/Yei6AVtfcs7snATEIz+fhJhez/9IDwFI91xowDIBl8cccUzTgTHfcxDMAjot9mi/YM4NvIGUMD7fm7FEzz0tZOTfmfS8+Omons59Bpj03NIqQFGOokz6wEG/UhxBKiiEtFoxOx2wLXlYCe8h85cvG4J2M9YnFRupkIKZgCyWiIqVSZdMSLOXoPrYwVaBkV8WkjTw8orDR4iVYT/yNLkQXBkYFwmp8dRAUxwVyTo6UtGm7ApGXDmjIoed2s6c+S2GGnAPVFTLKXf21kpUJ2KibOQ9r5cAwzrThrAUpE91KrstPLd67snjUoT+DIp2+lfYEq+X91OjrdkpdtHptzTfprJ6XolUY1KTojlzdxlyKepohaQeHJuYG+Q5PqIuUy+gmgAaCZdz2N0nsIYA33Ly5+zdsccRrkOdV0PthtRTA7p5b7JP4rGGkzayCaN/8h36A5tNHYcT17qLAAAAAElFTkSuQmCC" // Use actual Base64 or URL
                        alt="Crown"
                        width={28}
                        height={18}
                      />
                    </Box>
                    <Link
                      href={getOldLink(
                        '/subscribe/plans?ref=&source=Jobfeed&origin=profileMenu&scrollTo=none'
                      )}
                      sx={{color: '#000', textDecorationLine: 'none'}}
                    >
                      <Box display="flex" flexDirection="row">
                        <Box marginLeft={1.2}>
                          <Typography fontSize={14}>Go Premium</Typography>
                          <Typography fontSize={16} sx={{fontWeight: 'bold'}}>
                            Advance in your career with our Premium Services
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Button
                            size="small"
                            sx={{
                              fontSize: 12,
                              fontWeight: 'bold',
                              width: '54px',
                              backgroundColor: '#f5582d',
                              color: '#fff',
                              ':hover': {
                                backgroundColor: '#f5582d',
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Link>
                  </Box>
                )
              )}
              <List>
                {listData2?.map((option, index) => {
                  const myChatCount =
                    option['label'] === 'My Chat' &&
                    typeof chatCount === 'number' &&
                    chatCount > 0
                      ? `${option['label']} (${chatCount})`
                      : option['label'];
                  
                  const personalDetailsUrl = forwardQueryParamIfMatches('/registration/addPersonalDetails', 'ref', ['admin_login']);
                  
                  const updatedUrl =
                    option.label === 'Edit Profile'
                      ? profileStatus.personalDetails &&
                        profileStatus.resumeAttached &&
                        profileStatus.educationDetails &&
                        profileStatus.professionalDetails
                        ? option.url
                        : personalDetailsUrl
                      : option.url;

                  return (
                    <LinkItem
                      option={{
                        ...option,
                        label: myChatCount,
                        url: updatedUrl,
                      }}
                      index={index}
                      key={index}
                      sx={{
                        paddingY: expandProfile ? 1.5 : 1.8,
                        fontSize: '16px',
                        cursor: 'pointer',
                        color: '#555',
                        '&:hover': {
                          backgroundColor: '#eff3fa',
                        },
                        fontWeight: expandProfile ? 600 : 400,
                      }}
                    />
                  );
                })}
              </List>
            </Box>
          </Box>
        )}
      </Box>
      {!expandProfile && (
        <Link
          onClick={() => {
            OnLogoutBtnClick();
          }}
          sx={{
            textDecoration: 'none',
            color: '#555',
            width: '100%',
            height: '50px',
            justifyContent: 'flex-start',
            alignItems: 'center',
            display: 'flex',
            backgroundColor: '#f6f6f6',
            paddingTop: '2px',
            paddingLeft: 2.8,
            cursor: 'pointer',
          }}
        >
          <Image src={SwitchOff} alt="Logo" width={16} height={16} />
          <Typography marginLeft="9px">Log Out</Typography>
        </Link>
      )}
    </Box>
  );
};

export default ProfileDrop;
